import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../core/models/form-group-type';
import { ApplicationFormControlManagerService } from '../../services/application-form-control-manager/application-form-control-manager.service';

/**
 * Helper class for application validation. This should not have any service dependencies and should
 * NOT be mocked in tests.
 */
export class ApplicationValidationHelper {
    /**
     * Marks all required fields as touched if they are required OR if they fail formatting validation.
     * Applications do not enforce "required" fields on page navigation, but we still need to indicate to the user
     * that they need to fill out the required fields.
     */
    static markAllRequiredAsTouched<T>(
        formGroupType:
            | FormGroup<FormGroupType<T>>
            | FormGroup<NestedFormGroupType<T>>,
    ) {
        Object.keys(formGroupType.controls).forEach((field) => {
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            const control = (formGroupType as any).get(
                field,
            ) as AbstractControl | null;
            if (control) {
                if (control instanceof FormControl) {
                    if (
                        control.hasValidator(Validators.required) &&
                        !control.value
                    ) {
                        control.markAsTouched({ onlySelf: true });
                    } else if (control.value && control.invalid) {
                        control.markAsTouched({ onlySelf: true });
                    } else {
                        control.updateValueAndValidity({ onlySelf: true });
                    }
                } else if (control instanceof FormArray) {
                    control.controls.forEach((arrayControl) => {
                        this.markAllRequiredAsTouched(
                            arrayControl as FormGroup,
                        );
                    });
                }
            }
        });
    }

    /**
     * Checks the passed in `fieldKeys` on the form to see whether any of them have been touched.
     */
    static checkIfAnyFieldsIsTouched<T>(
        formGroupType: FormGroup<FormGroupType<T>>,
        fieldKeys: (keyof T & string)[],
    ): boolean {
        return fieldKeys.some((key) => formGroupType.get(key)?.touched);
    }

    /**
     * Will grab the controls from the form group and enable them as well as adding a required validator to them.
     */
    static enableAndRequireFormControls<T>(
        formGroupType: FormGroup<FormGroupType<T>>,
        applicationFormControlManager: ApplicationFormControlManagerService,
        controlsToBeEnabled: (keyof T & string)[],
    ): void {
        controlsToBeEnabled.forEach((controlName) => {
            const control = formGroupType.get(controlName);
            if (control) {
                applicationFormControlManager.enable(
                    control,
                    { emitEvent: false },
                    () => {
                        control.addValidators(Validators.required);
                        control.updateValueAndValidity({ emitEvent: false });
                    },
                );
            }
        });
    }
}
